import React from 'react';

export interface TabProps {
  title: React.ReactNode;
  eventKey: number;
  disabled?: boolean;
  children?: React.ReactNode;
}

export interface TabsProps {
  id?: string;
  className?: string;
  bsStyle?: string;
  defaultActiveKey?: number;
  activeKey?: number; // Allow controlled operation
  children: React.ReactElement<TabProps>[];
  onSelect?: (eventKey: number) => void;
  mountOnEnter?: boolean; // New prop to match react-bootstrap behavior
}

export interface TabsPropsWithHTML
  extends TabsProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect' | 'children'> {}

const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

const Tabs = React.forwardRef<HTMLDivElement, TabsPropsWithHTML>(
  (
    {
      id,
      className,
      bsStyle,
      defaultActiveKey = 1,
      activeKey, // Accept activeKey prop for controlled operation
      children,
      onSelect,
      mountOnEnter = false, // Default to false to match react-bootstrap
      ...props
    },
    ref,
  ) => {
    // Use activeKey prop if provided (controlled), otherwise use internal state (uncontrolled)
    const [internalActiveKey, setInternalActiveKey] = React.useState(
      defaultActiveKey,
    );
    const currentActiveKey =
      activeKey !== undefined ? activeKey : internalActiveKey;

    // Track which tabs have been visited (for mountOnEnter behavior)
    const [visitedTabs, setVisitedTabs] = React.useState<Set<number>>(
      new Set([currentActiveKey]),
    );

    // Extract titles and content from Tab children
    const tabs =
      React.Children.map(children, child => {
        if (!React.isValidElement(child) || child.type !== Tab) return null; // Ensure child is a Tab component
        const tabProps = child.props as TabProps;
        return {
          title: tabProps.title,
          eventKey: tabProps.eventKey,
          disabled: tabProps.disabled || false,
          content: tabProps.children,
        };
      })?.filter(Boolean) || []; // Filter out any null values if some children are not valid Tab elements

    const handleSelect = (eventKey: number) => {
      // Find the tab to check if it's disabled
      const tab = tabs.find(t => t.eventKey === eventKey);
      if (tab?.disabled) {
        return; // Don't select disabled tabs
      }

      if (activeKey === undefined) {
        // Uncontrolled mode - update internal state
        setInternalActiveKey(eventKey);
      }

      // If mountOnEnter is true, add this tab to visited tabs
      if (mountOnEnter) {
        setVisitedTabs(prev => new Set(prev).add(eventKey));
      }

      // Always call onSelect if provided
      if (onSelect) {
        onSelect(eventKey);
      }
    };

    if (!tabs || tabs.length === 0) return null;

    const isTabsStyle = bsStyle === 'tabs';
    const isCustomStyle = className?.includes('tab-custom');

    const tabHeaderStyle: React.CSSProperties = {
      display: 'flex',
      // Align tabs to the left when bsStyle is 'tabs', otherwise center
      justifyContent: isTabsStyle ? 'flex-start' : 'center',
      background: 'transparent',
      padding: isTabsStyle || isCustomStyle ? '0' : '10px 0',
      ...(isTabsStyle && {
        borderBottom: '1px solid #ddd',
      }),
      ...(isCustomStyle && {
        borderBottom: '1px solid #ddd',
        display: 'inline-flex',
      }),
    };

    const tabButtonStyle = (
      isActive: boolean,
      isDisabled = false,
    ): React.CSSProperties => {
      if (isTabsStyle) {
        return {
          padding: '10px 15px',
          marginRight: '2px',
          marginBottom: '-1px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          border: isActive ? '1px solid #ddd' : 'none',
          borderBottom: isActive ? '1px solid #fff' : '1px solid #ddd',
          background: '#fff',
          outline: 'none',
          fontWeight: 400,
          color: isDisabled ? '#999' : isActive ? '#333' : '#337ab7',
          position: 'relative',
          transition: 'background-color 0.2s, color 0.2s',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          opacity: isDisabled ? 0.6 : 1,
        };
      }

      if (isCustomStyle) {
        return {
          padding: '11px 15px',
          marginRight: '2px',
          marginBottom: '-1px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          border: isActive ? '1px solid #ddd' : '1px solid transparent',
          borderBottom: isActive
            ? '1px solid transparent'
            : '1px solid transparent',
          background: isActive ? 'rgba(51, 122, 183, 0.125)' : 'transparent',
          outline: 'none',
          fontWeight: 400,
          fontSize: '13px',
          color: isDisabled ? '#999' : 'rgb(51, 122, 183)',
          position: 'relative',
          transition: 'background-color 0.2s, color 0.2s, border-radius 0.2s',
          borderTopLeftRadius: isActive ? '4px' : '0',
          borderTopRightRadius: isActive ? '4px' : '0',
          opacity: isDisabled ? 0.6 : 1,
        };
      }

      // Styles for default bsStyle (not 'tabs')
      return {
        padding: '10px 0',
        margin: '0 20px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        border: 'none',
        background: 'transparent',
        outline: 'none',
        fontWeight: 400,
        color: isDisabled ? '#999' : 'rgb(51, 122, 183)',
        position: 'relative',
        transition: 'color 0.2s',
        opacity: isDisabled ? 0.6 : 1,
      };
    };

    const tabBodyStyle: React.CSSProperties = {
      padding: isTabsStyle ? '15px' : 0,
      background: 'transparent',
      border: 'none',
      borderTop: isTabsStyle ? 'none' : undefined, // Important for bsStyle="tabs"
      backgroundColor: isTabsStyle ? '#fff' : 'transparent',
      textAlign: 'left', // Ensure content is left-aligned regardless of parent centering
    };

    const activeTab = tabs.find(tab => tab.eventKey === currentActiveKey);

    const getTabButtonClass = (eventKey: number, isDisabled: boolean) => {
      let classes = 'tab-button';
      if (isTabsStyle) {
        classes += ' tabs-style';
      }
      if (isCustomStyle) {
        classes += ' custom-style';
      }
      if (eventKey === currentActiveKey) {
        classes += ' active';
      }
      if (isDisabled) {
        classes += ' disabled';
      }
      return classes;
    };

    // Determine what content to render based on mountOnEnter
    const renderTabContent = () => {
      if (!mountOnEnter) {
        // Normal behavior - always render active tab content
        return activeTab ? activeTab.content : null;
      }
      // mountOnEnter behavior - only render tabs that have been visited
      return tabs.map(tab => {
        const isActive = tab.eventKey === currentActiveKey;
        const hasBeenVisited = visitedTabs.has(tab.eventKey);

        if (!hasBeenVisited && !isActive) {
          return null; // Don't render unvisited tabs
        }

        return (
          <div
            key={String(tab.eventKey)}
            style={{ display: isActive ? 'block' : 'none' }}
            role="tabpanel"
            id={`tabpanel-${String(tab.eventKey)}`}
            aria-labelledby={`tab-${String(tab.eventKey)}`}
          >
            {tab.content}
          </div>
        );
      });
    };

    return (
      <div id={id} className={className} ref={ref} {...props}>
        <style>
          {`
          .tab-button {
            position: relative; /* Base for pseudo-elements */
          }

          /* Styles for default tab underline animation */
          .tab-button:not(.tabs-style):not(.custom-style)::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            width: 0;
            height: 2px;
            background-color: rgb(51, 122, 183);
            transition: width 0.3s ease, left 0.3s ease;
          }

          .tab-button:not(.tabs-style):not(.custom-style).active::after {
            width: 100%;
            left: 0;
          }

          .tab-button:not(.tabs-style):not(.custom-style):not(.active):hover::after {
            width: 100%;
            left: 0;
          }

          /* Hover effect for bsStyle="tabs" */
          /* Non-active tabs with bsStyle="tabs" should turn grey on hover */
          .tab-button.tabs-style:not(.active):not(.disabled):hover {
            background-color: #eeeeee !important; /* A slightly darker grey for hover, or your preferred grey */
            /* color: #000; */ /* Optionally change text color on hover */
          }

          /* Hover effect for custom-style tabs */
          .tab-button.custom-style:not(.active):not(.disabled):hover {
            background-color: #eeeeee !important;
            border-top-left-radius: 4px !important;
            border-top-right-radius: 4px !important;
          }

          /* Ensure active tab with bsStyle="tabs" maintains its active styles and z-index */
          .tab-button.tabs-style.active {
            z-index: 1; /* Keeps the active tab's bottom border above the container's border */
            background-color: #fff !important; /* Explicitly set active background */
            border-bottom-color: #fff !important; /* Make bottom border blend with content area */
          }

          /* Disabled tab styles */
          .tab-button.disabled {
            pointer-events: none;
          }

          /* Disable hover effects for disabled tabs */
          .tab-button.disabled::after {
            display: none !important;
          }
        `}
        </style>
        <div style={tabHeaderStyle} role="tablist">
          {tabs.map(tab => {
            // Hide disabled tabs when using bsStyle="tabs"
            if (isTabsStyle && tab.disabled) {
              return null;
            }

            return (
              <button
                key={String(tab.eventKey)} // Assuming eventKey is unique and stable
                type="button"
                role="tab"
                className={getTabButtonClass(tab.eventKey, tab.disabled)}
                style={tabButtonStyle(
                  tab.eventKey === currentActiveKey,
                  tab.disabled,
                )}
                // Use the new handler for click events
                onClick={() => handleSelect(tab.eventKey)}
                aria-selected={tab.eventKey === currentActiveKey}
                aria-controls={`tabpanel-${String(tab.eventKey)}`}
                aria-disabled={tab.disabled}
                id={`tab-${String(tab.eventKey)}`}
                tabIndex={
                  tab.disabled ? -1 : tab.eventKey === currentActiveKey ? 0 : -1
                } // For accessibility
                disabled={tab.disabled}
              >
                {tab.title}
              </button>
            );
          })}
        </div>
        <div
          style={tabBodyStyle}
          role="tabpanel"
          id={`tabpanel-${String(currentActiveKey)}`} // Ensure this ID is unique if multiple Tabs components are on a page
          aria-labelledby={`tab-${String(currentActiveKey)}`} // Matches the active tab button's ID
        >
          {renderTabContent()}
        </div>
      </div>
    );
  },
);

// Add display name for easier debugging
Tabs.displayName = 'Tabs';

export { Tab, Tabs };
